import { Link } from "react-router-dom";

import type { LastFlightDetail } from "@/types/api";
import { formatDate } from "@/lib/format";
import type { WidgetComponentProps } from "../types";
import { useSummaryQuery } from "../useSummaryQuery";

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="ai-badge ai-badge--none">No feedback</span>;
  if (status === "ready") return <span className="ai-badge ai-badge--ready">Ready</span>;
  if (status === "processing")
    return <span className="ai-badge ai-badge--processing">Processing</span>;
  return <span className="ai-badge ai-badge--failed">Failed</span>;
}

function FlightMeta({ flight }: { flight: LastFlightDetail }) {
  const flightAt =
    (flight as unknown as { flight_at?: string | null }).flight_at ??
    (flight as unknown as { start_time?: string | null }).start_time ??
    null;
  const label = flight.session_name ?? `Flight ${flight.id}`;
  return (
    <span className="ai-feedback__flight" title={label}>
      <Link to={`/flights/${flight.id}`} className="ai-feedback__flight-link">
        {label}
      </Link>
      {flightAt ? (
        <span className="ai-feedback__flight-date"> · {formatDate(flightAt)}</span>
      ) : null}
    </span>
  );
}

function AiSmall({ flight }: { flight: LastFlightDetail }) {
  return (
    <div className="ai-feedback ai-feedback--s">
      <div className="ai-feedback__row">
        <p className="eyebrow">Latest flight feedback</p>
        <StatusBadge status={flight.ai_feedback_status} />
      </div>
      <FlightMeta flight={flight} />
      {flight.ai_feedback_summary ? (
        <p className="ai-feedback__summary ai-feedback__summary--clamp-2">
          {flight.ai_feedback_summary}
        </p>
      ) : (
        <p className="widget-empty">
          {flight.ai_feedback_status === "processing"
            ? "Analyzing…"
            : "No summary yet."}
        </p>
      )}
    </div>
  );
}

function AiMedium({ flight }: { flight: LastFlightDetail }) {
  return (
    <div className="ai-feedback ai-feedback--m">
      <div className="ai-feedback__head">
        <div className="ai-feedback__head-text">
          <p className="eyebrow">Latest flight feedback</p>
          <FlightMeta flight={flight} />
        </div>
        <StatusBadge status={flight.ai_feedback_status} />
      </div>
      <p className="ai-feedback__summary">
        {flight.ai_feedback_summary ?? "No summary available yet."}
      </p>
      <Link to={`/flights/${flight.id}`} className="text-link">
        Full analysis →
      </Link>
    </div>
  );
}

function AiLarge({ flight }: { flight: LastFlightDetail }) {
  // Split summary into sentences/bullets heuristically
  const summary = flight.ai_feedback_summary ?? "";
  const sentences = summary
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const positives = sentences.filter(
    (s) => /\b(good|excellent|strong|maintained|avoided|consistent)\b/i.test(s),
  );
  const negatives = sentences.filter(
    (s) => /\b(aggressive|rapid|improve|focus on|stability|reduce)\b/i.test(s),
  );

  return (
    <div className="ai-feedback ai-feedback--l">
      <div className="ai-feedback__head">
        <div>
          <p className="eyebrow">Latest flight feedback</p>
          <h3 className="ai-feedback__title">
            {flight.session_name ?? `Flight ${flight.id}`}
          </h3>
          <FlightMeta flight={flight} />
        </div>
        <StatusBadge status={flight.ai_feedback_status} />
      </div>
      <p className="ai-feedback__summary">
        {summary || "No summary available yet."}
      </p>
      {(positives.length > 0 || negatives.length > 0) && (
        <div className="ai-feedback__split">
          {positives.length > 0 && (
            <div className="ai-feedback__column ai-feedback__column--plus">
              <p className="eyebrow">Strengths</p>
              <ul>
                {positives.slice(0, 3).map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
          )}
          {negatives.length > 0 && (
            <div className="ai-feedback__column ai-feedback__column--minus">
              <p className="eyebrow">Focus areas</p>
              <ul>
                {negatives.slice(0, 3).map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <Link to={`/flights/${flight.id}`} className="text-link">
        Full analysis →
      </Link>
    </div>
  );
}

export function AiFeedbackWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();
  if (isLoading) return <p className="widget-empty">Loading…</p>;
  const flight = data?.last_flight;
  if (!flight) return <p className="widget-empty">No flights yet.</p>;
  if (sizeVariant === "s") return <AiSmall flight={flight} />;
  if (sizeVariant === "l") return <AiLarge flight={flight} />;
  return <AiMedium flight={flight} />;
}
